import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateTemplateInput,
  Template,
  TemplateListResponse,
  TemplateState,
  UpdateTemplateInput,
  UploadLogoResponse,
} from '../types';
import { templateService } from './services';

interface ListTemplatesParams {
  nome?: string;
  status?: string;
  tipo?: string;
  page?: number;
  pageSize?: number;
}

export const listarTemplates = createAsyncThunk<
  TemplateListResponse,
  ListTemplatesParams | undefined
>('template/listar', async (params, { rejectWithValue }) => {
  try {
    return await templateService.list(params);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao listar templates';
    return rejectWithValue(message);
  }
});

export const buscarTemplate = createAsyncThunk<Template, string>(
  'template/buscar',
  async (id, { rejectWithValue }) => {
    try {
      return await templateService.getById(id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao buscar template';
      return rejectWithValue(message);
    }
  },
);

export const buscarPreviewTemplatePadrao = createAsyncThunk<string, void>(
  'template/buscarPreviewPadrao',
  async (_, { rejectWithValue }) => {
    try {
      return await templateService.getPreviewPadraoArbitralis();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar o template padrão';
      return rejectWithValue(message);
    }
  },
);

export const buscarModeloPortal = createAsyncThunk<
  string,
  void,
  { state: { template: TemplateState }; rejectValue: string }
>(
  'template/buscarModeloPortal',
  async (_, { rejectWithValue }) => {
    try {
      return await templateService.getModeloPortal();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Erro ao carregar a pré-visualização do template';
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const status = getState().template.modeloPortalStatus;
      return status === 'idle' || status === 'failed';
    },
  },
);

export const criarTemplate = createAsyncThunk<Template, CreateTemplateInput>(
  'template/criar',
  async (data, { rejectWithValue }) => {
    try {
      const result = await templateService.create(data);
      return result.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao criar template';
      return rejectWithValue(message);
    }
  },
);

export const atualizarTemplate = createAsyncThunk<
  Template,
  { id: string; data: UpdateTemplateInput }
>('template/atualizar', async ({ id, data }, { rejectWithValue }) => {
  try {
    const result = await templateService.update(id, data);
    return result.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao atualizar template';
    return rejectWithValue(message);
  }
});

export const publicarTemplate = createAsyncThunk<Template, string>(
  'template/publicar',
  async (id, { rejectWithValue }) => {
    try {
      const result = await templateService.publish(id);
      return result.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao publicar template';
      return rejectWithValue(message);
    }
  },
);

export const excluirTemplate = createAsyncThunk<string, string>(
  'template/excluir',
  async (id, { rejectWithValue }) => {
    try {
      await templateService.delete(id);
      return id;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao excluir template';
      return rejectWithValue(message);
    }
  },
);

interface CheckTemplateNameParams {
  nome: string;
  canal: string;
  excludeId?: string;
}

export const checkTemplateName = createAsyncThunk<{ available: boolean }, CheckTemplateNameParams>(
  'template/checkName',
  async (params, { rejectWithValue }) => {
    try {
      return await templateService.checkName(params);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao verificar nome do template';
      return rejectWithValue(message);
    }
  },
);

export const uploadLogo = createAsyncThunk<UploadLogoResponse, File>(
  'template/uploadLogo',
  async (file, { rejectWithValue }) => {
    try {
      return await templateService.uploadLogo(file);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao fazer upload do logo';
      return rejectWithValue(message);
    }
  },
);
